// src/contexts/CartContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage if available
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  // Initialize payment method from localStorage if available
  const [paymentMethod, setPaymentMethod] = useState(() => {
    const savedPaymentMethod = localStorage.getItem('paymentMethod');
    return savedPaymentMethod || 'vodafone_cash'; // Default to vodafone_cash
  });

  // Calculate cart count
  const [cartCount, setCartCount] = useState(0);
  
  // Update cart count whenever cartItems changes
  useEffect(() => {
    const count = cartItems.reduce((total, item) => total + item.quantity, 0);
    setCartCount(count);
  }, [cartItems]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);
  
  // Save payment method to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('paymentMethod', paymentMethod);
  }, [paymentMethod]);

  // Add item to cart
  const addToCart = (item) => {
    setCartItems(currentItems => {
      // Check if this exact product variant already exists in cart
      const existingItemIndex = currentItems.findIndex(
        cartItem => 
          cartItem.id === item.id && 
          cartItem.colorId === item.colorId && 
          cartItem.size === item.size
      );

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        const updatedItems = [...currentItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + item.quantity
        };
        return updatedItems;
      } else {
        // Add new item
        return [...currentItems, item];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (productId, colorId, size) => {
    setCartItems(currentItems => 
      currentItems.filter(item => 
        !(item.id === productId && item.colorId === colorId && item.size === size)
      )
    );
  };

  // Update item quantity
  const updateQuantity = (productId, colorId, size, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId, colorId, size);
      return;
    }

    setCartItems(currentItems =>
      currentItems.map(item =>
        (item.id === productId && item.colorId === colorId && item.size === size)
          ? { ...item, quantity: parseInt(newQuantity) }
          : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      cartCount,
      addToCart, 
      removeFromCart, 
      updateQuantity,
      clearCart,
      paymentMethod,
      setPaymentMethod
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
/*
import React, { createContext, useContext, useEffect, useState } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  const updateCart = (newCart) => {
    setCartItems(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const addToCart = (product, size, quantity) => {
    const existingItem = cartItems.find(item => 
      item.product.id === product.id && item.size === size
    );

    if (existingItem) {
      const updatedCart = cartItems.map(item =>
        item.product.id === product.id && item.size === size
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
      updateCart(updatedCart);
    } else {
      updateCart([...cartItems, { product, size, quantity }]);
    }
  };

  const removeFromCart = (productId, size) => {
    const updatedCart = cartItems.filter(item => 
      !(item.product.id === productId && item.size === size)
    );
    updateCart(updatedCart);
  };

  const clearCart = () => updateCart([]);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      cartCount: cartItems.reduce((sum, item) => sum + item.quantity, 0)
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
*/
/*****************************************************************************/
/*
// Updated CartProvider component with cart validation
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient'; // Add supabase import

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const validateCart = async () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          const productIds = parsedCart.map(item => item.product.id);
          
          // Fetch existing product IDs
          const { data: existingProducts, error } = await supabase
            .from('products')
            .select('id')
            .in('id', productIds);

          if (error) throw error;

          const existingProductIds = new Set(existingProducts.map(p => p.id));
          const validatedCart = parsedCart.filter(item => 
            existingProductIds.has(item.product.id)
          );

          // Update cart state and localStorage only if there's a change
          if (validatedCart.length !== parsedCart.length) {
            setCartItems(validatedCart);
            localStorage.setItem('cart', JSON.stringify(validatedCart));
          } else {
            setCartItems(parsedCart);
          }
        } catch (error) {
          console.error('Error validating cart:', error);
          // If error occurs, set cart from localStorage anyway
          setCartItems(JSON.parse(savedCart));
        }
      }
    };

    validateCart();
  }, []);

  const updateCart = (newCart) => {
    setCartItems(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const addToCart = (product, size, quantity) => {
    const existingItem = cartItems.find(item => 
      item.product.id === product.id && item.size === size
    );

    if (existingItem) {
      const updatedCart = cartItems.map(item =>
        item.product.id === product.id && item.size === size
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
      updateCart(updatedCart);
    } else {
      updateCart([...cartItems, { product, size, quantity }]);
    }
  };

  const removeFromCart = (productId, size) => {
    const updatedCart = cartItems.filter(item => 
      !(item.product.id === productId && item.size === size)
    );
    updateCart(updatedCart);
  };

  const clearCart = () => updateCart([]);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      cartCount: cartItems.reduce((sum, item) => sum + item.quantity, 0)
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
*/
